import { getItemUniqueKey, getPackageId } from 'checkout/lib/utils/accommodation/cart'
import { getOptInBundleInclusionsFromRate } from 'checkout/lib/utils/accommodation/bundles'
import type { IntlShape } from 'react-intl'

function occupancyFromSession(session: App.AccommodationEnquirySession): App.Occupants {
  const { occupancy } = session.data
  return {
    adults: occupancy.adults,
    children: occupancy.children,
    teenagers: occupancy.teenagers,
    infants: occupancy.infants,
    childrenAge: occupancy.childrenAges,
  }
}

function emptyTotals(): App.Checkout.ItemViewTotals {
  return {
    price: 0,
    memberPrice: 0,
    value: 0,
    memberValue: 0,
    surcharge: 0,
    extraGuestSurcharge: 0,
    taxesAndFees: 0,
  }
}

/**
 * Cart-review totals from the shopped snapshot on the item.
 * Live enquiry rates are not used here — sessions expire after ~2h, so review
 * prices the saved sell total. Checkout re-enquires before purchase.
 *
 * `luxPlusTotal` is exclusive of property fees, matching `sellTotal`.
 * `getOverallUserCartTotals` adds `otherFees.propertyFees` onto both.
 */
export function getAccommodationV2CartItemTotals(
  item: App.Checkout.AccommodationV2Item,
): App.Checkout.ItemViewTotals {
  const propertyFees = item.propertyFees ?? 0
  const memberHotelPrice = item.luxPlusTotal ?? 0

  return {
    price: item.sellTotal,
    memberPrice: memberHotelPrice,
    value: item.sellTotal,
    memberValue: 0,
    surcharge: 0,
    extraGuestSurcharge: 0,
    taxesAndFees: 0,
    ...(propertyFees ? { otherFees: { propertyFees, extraGuestSurcharge: 0 } } : {}),
  }
}

export function getAccommodationV2ItemView(
  intl: IntlShape,
  item: App.Checkout.AccommodationV2Item,
  sessionRecord: App.CheckoutState['accommodationSessions'][string] | undefined,
  property?: App.Property.Property,
): App.WithDataStatus<App.Checkout.AccommodationV2AccommodationItemView> {
  const session = sessionRecord?.data
  const rate = sessionRecord?.rate
  const outcome = sessionRecord?.outcome
  const room = property?.rooms[item.roomId] ?? (session ? property?.rooms[session.data.room.roomId] : undefined)
  const occupancy = session ? occupancyFromSession(session) : item.occupancy
  const soldOut = outcome === 'unavailable' || (!rate && outcome != null)
  const image = room?.images[0] ?? property?.images[0] ?? {}
  const primaryTitle = room?.name || rate?.name || ''
  const propertyFees = session?.data.pricing.propertyFees ?? rate?.totals.propertyFees ?? 0
  // svc-order charges `session.data.pricing.sellTotal`. Property fees are stored
  // separately from `taxesAndFees` and shown as due-at-property via `otherFees`,
  // not added into `price`.
  const price = session?.data.pricing.sellTotal ?? rate?.totals.sellingPrice ?? 0
  const memberPrice = session?.data.pricing.luxPlusTotal ?? rate?.luxPlusTotals?.sellingPrice ?? 0

  const view: App.Checkout.AccommodationV2AccommodationItemView = {
    kind: 'accommodationV2',
    packageId: getPackageId(item),
    uniqueKey: getItemUniqueKey(item),
    item,
    image,
    primaryTitle,
    occupancy,
    propertyId: item.propertyId,
    rate,
    inclusions: rate?.inclusions,
    bonusInclusions: rate?.bonusInclusions,
    bundleInclusions: item.bundleOptin ? undefined : rate?.bundleInclusions,
    optInBundleInclusions: item.bundleOptin
      ? getOptInBundleInclusionsFromRate(rate, item)
      : undefined,
    removeLabel: intl.formatMessage({
      id: 'WmkzQGWSf9s2KSRQdBJ4',
      defaultMessage: 'Remove',
      description: 'Remove button label for accommodationV2 checkout item',
    }),
    soldOut,
    enquiryError: sessionRecord?.error,
    loyalty: {},
    title: primaryTitle,
    totals: session || rate ? {
      price,
      memberPrice,
      value: rate?.totals.valuedUpToPrice ?? price,
      memberValue: rate?.luxPlusTotals?.valuedUpToPrice ?? memberPrice,
      surcharge: 0,
      extraGuestSurcharge: 0,
      taxesAndFees: rate?.totals.taxesAndFees ?? 0,
      otherFees: propertyFees ? { propertyFees, extraGuestSurcharge: 0 } : undefined,
    } : emptyTotals(),
  }

  const hasRequiredData = !!session && !!property && !sessionRecord?.error && (outcome === 'unavailable' || !!rate)

  return { hasRequiredData, data: view }
}
